import { JSX as IonicJSX } from '@ionic/core';
import { IonInput, IonText } from '@ionic/react';
import { PropsWithChildren } from 'react';

import { Icon, IconName } from '@/components/Icon';
import { IconRotate, IconSize } from '@/components/Icon/types';
import styles from '@/components/Input/index.module.css';

import { mergeClassNames } from '@/utils/mergeClassNames';

type IonicInputProps = IonicJSX.IonInput;

interface InputProps extends IonicInputProps {
  leadingIcon?: IconName;
  leadingIconDirection?: IconRotate;
  trailingIcon?: IconName;
  trailingIconDirection?: IconRotate;
  /** Should the input be shown in an error state */
  hasError?: boolean;
  className?: string;
  variant?: 'primary' | 'secondary' | 'light';
  isSearch?: boolean;
  parentRef?: React.RefObject<HTMLIonInputElement>;
  onClick?: (event: React.MouseEvent<HTMLIonInputElement>) => void;
}

export const Input = ({
  leadingIcon,
  leadingIconDirection,
  trailingIcon,
  trailingIconDirection,
  hasError,
  className,
  children,
  variant = 'primary',
  required,
  label,
  isSearch,
  parentRef,
  ...props
}: PropsWithChildren<InputProps>) => {
  return (
    <IonInput
      className={mergeClassNames(
        styles.input,
        hasError && styles.error,
        className,
        leadingIcon && styles['has-leading-icon'],
        trailingIcon && styles['has-trailing-icon'],
        styles[variant],
        styles[props.type ?? ''],
        isSearch && styles.isSearch,
      )}
      ref={parentRef}
      required={required}
      {...props}>
      <div className={styles.label} slot='label'>
        {label} {required && <IonText className={styles.requiredStar}>*</IonText>}
      </div>
      {leadingIcon && (
        <Icon
          className={mergeClassNames(styles.icon, styles['icon-left'], styles[variant])}
          direction={leadingIconDirection}
          name={leadingIcon}
          size={isSearch ? IconSize.large : IconSize.small}
        />
      )}
      {children}
      {trailingIcon && (
        <Icon
          className={mergeClassNames(styles.icon, styles['icon-right'])}
          direction={trailingIconDirection}
          name={trailingIcon}
          size={IconSize.small}
        />
      )}
    </IonInput>
  );
};
